/*
  ba-h-scroller.js
  WHAT: Shared prev/next button behaviour for horizontal scroll galleries +
        progress-bar fill that tracks scrollLeft / max-scroll.
  WHY:  Story-cards and reviews-cards both need arrow controls AND a progress
        bar (mock 08:182-196, 260-290). One JS, one CSS hook.
  USAGE:
    <div data-h-scroller="story">…cards…</div>
    <div data-h-scroller-progress="story"><div data-h-scroller-progress-fill></div></div>
    <button data-h-scroller-prev data-h-scroller-controls="story">‹</button>
    <button data-h-scroller-next data-h-scroller-controls="story">›</button>

    OR shorter: wrap the prev/next pair in any element with
    `data-h-scroller-controls="<key>"` and the buttons inside don't need their own key.
  WHEN: Added 2026-05-28 with PDP horizontal-scroller polish. Progress-bar updating
        added 2026-05-29 for mock-fidelity bottom-nav layout.
*/

(function () {
  'use strict';

  function getScrollerFor(key) {
    return document.querySelector('[data-h-scroller="' + key + '"]');
  }

  function getProgressFillFor(key) {
    var bar = document.querySelector('[data-h-scroller-progress="' + key + '"]');
    return bar ? bar.querySelector('[data-h-scroller-progress-fill]') : null;
  }

  function updateProgress(scroller, fillEl) {
    if (!scroller || !fillEl) return;
    var max = scroller.scrollWidth - scroller.clientWidth;
    var pct = max <= 0 ? 100 : Math.min(100, Math.max(0, (scroller.scrollLeft / max) * 100));
    fillEl.style.width = pct + '%';
  }

  function scrollByAmount(scroller, direction) {
    if (!scroller) return;
    var firstChild = scroller.firstElementChild;

    /* Step = as many WHOLE cards as fit, minus one.
       Owner 2026-08-21: "the reviews should scroll across the screen when the
       arrow is clicked, not one at a time but at least 2 or 3. as you move
       across they should move the ones seen to the left and still show them and
       not cut them off."

       Two things that matters for:
       • Advancing by whole cards means a card never comes to rest half-cut at
         the edge — the old behaviour stepped one card and could leave a sliver.
       • Keeping ONE card overlapping from the previous view is what gives the
         sense of things sliding left rather than the rail jumping to an
         unrelated set. It also keeps a card in view to orient against.

       Falls back to one card if only one fits (narrow phones), and to 85% of the
       rail if the cards have no measurable width yet. */
    var step;
    if (firstChild && firstChild.offsetWidth > 0) {
      var gap = 16;
      var cardW = firstChild.offsetWidth + gap;
      var perView = Math.max(1, Math.floor(scroller.clientWidth / cardW));
      var advance = perView > 1 ? perView - 1 : 1;
      step = cardW * advance;
    } else {
      step = Math.round(scroller.clientWidth * 0.85);
    }

    var max = scroller.scrollWidth - scroller.clientWidth;

    // Nothing to scroll — bail rather than fight a 0-width range.
    if (max <= 0) return;

    /* Wrap around at both ends. Owner 2026-08-21: "when they get to the [last]
       they wrap back to the start."

       The tolerance was 4px and that was too tight to be reliable: smooth
       scrolling settles on fractional scrollLeft values, and a scroll-snap
       container can come to rest a few pixels short of max. When it did, the
       wrap never fired and the arrow just went dead at the end of the rail.
       12px is inside one card gap (16px) so it cannot trigger early, but wide
       enough to absorb sub-pixel rounding. */
    var EDGE = 12;

    if (direction > 0 && scroller.scrollLeft >= max - EDGE) {
      scroller.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (direction < 0 && scroller.scrollLeft <= EDGE) {
      scroller.scrollTo({ left: max, behavior: 'smooth' });
    } else {
      scroller.scrollBy({ left: direction * step, behavior: 'smooth' });
    }
  }

  document.addEventListener('click', function (e) {
    var prev = e.target.closest('[data-h-scroller-prev]');
    var next = e.target.closest('[data-h-scroller-next]');
    if (!prev && !next) return;

    e.preventDefault();
    var button = prev || next;
    var controlsHost = button.closest('[data-h-scroller-controls]');
    var key = controlsHost ? controlsHost.getAttribute('data-h-scroller-controls') : button.getAttribute('data-h-scroller-controls');
    if (!key) return;
    var scroller = getScrollerFor(key);
    scrollByAmount(scroller, prev ? -1 : 1);
  });

  // Bind scroll → progress for every scroller present on the page.
  function bindProgressForAllScrollers() {
    document.querySelectorAll('[data-h-scroller]').forEach(function (scroller) {
      var key = scroller.getAttribute('data-h-scroller');
      var fillEl = getProgressFillFor(key);
      if (!fillEl) return;
      var tick = function () { updateProgress(scroller, fillEl); };
      scroller.addEventListener('scroll', tick, { passive: true });
      window.addEventListener('resize', tick);
      tick();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindProgressForAllScrollers);
  } else {
    bindProgressForAllScrollers();
  }
})();
